import {Divider, Fade, ListItemText, Stack, Typography, useTheme} from '@mui/material';
import React from 'react';
import walletImageAdvance from 'presentation/assets/images/wallet-background-advance.png';
import walletImageInfinity from 'presentation/assets/images/wallet-background-infinity.png';
import walletImagePremium from 'presentation/assets/images/wallet-background-premium.png';
import walletImageGold from 'presentation/assets/images/wallet-background-ouro.svg';
import walletHeaderGold from 'presentation/assets/images/wallet-tarja-ouro.svg';
import walletImageSilver from 'presentation/assets/images/wallet-background-prata.svg';
import walletHeaderSilver from 'presentation/assets/images/wallet-tarja-prata.svg';
import walletImageDiamond from 'presentation/assets/images/wallet-background-diamante.png';
import walletHeaderDiamond from 'presentation/assets/images/wallet-tarja-diamante.svg';
import {AlertInfo, ProductLogo, TagChip} from 'presentation/components';
import {DateFormatEnum, FeaturesEnum} from 'presentation/constants';
import {useContractStatus} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {planTypeOptions, useMediaQueries} from 'presentation/pages';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {translations} from 'presentation/translations-new';
import {formatText, stringfyDate} from 'presentation/utils';
import {TypesOfPlansEnum} from '../../constants';
import {CopyButton} from '../CopyButton';
import {StyledBox, StyledCard, StyledCardHeader, StyledChip} from './styles';
import {HealthCardProps, PlanTypeConfig, SupportedPlanType} from './types';
import {NEW_WALLET_PLAN_TYPES} from 'presentation/utils/getCardBackgroundImage/constants';

const {cancelContractIn, cancelContract, suspendedContractAlert} = translations['pt-br'].commons;
const {cardCode, network, name, plan} = translations['pt-br'].privateFlow.healthCard.cardData;

export function HealthCardNotreDame({data, hiddenCopyIcon}: HealthCardProps) {
	const {
		nmPlano,
		nmSocialUsuarioC,
		nmUsuarioC,
		tipoUsuarioC,
		cdUsuarioC,
		cdModeloCarteira,
		redeCarteirinha,
	} = data;

	const {brand} = useTheme();
	const {isMobile} = useMediaQueries();
	const {isValid, contractCancellationDate, isContractSuspended} = useContractStatus();
	const {isEnabled: isNewWalletsEnabled} = useFeatureFlag(FeaturesEnum.PORTAL_NEW_WALLETS_PPO);

	const {
		beneficiary: {tipoPlanoC},
	} = useAccountDataStore(store => store.accountData);
	const formattedUserName = formatText(nmSocialUsuarioC || nmUsuarioC);
	const formattedChipLabel = formatText(tipoUsuarioC);
	const cancelContractLabel = contractCancellationDate ? cancelContractIn : cancelContract;

	const alertLabel = !isValid ? cancelContractLabel : suspendedContractAlert;
	const alertDate = !isValid ? contractCancellationDate : null;

	const planTypeMap: Record<SupportedPlanType, PlanTypeConfig> = {
		[TypesOfPlansEnum.NOTREDAME_SAUDE]: {
			backgroundImage: `url(${walletImagePremium})`,
			invertColor: false,
			invertPlanTypeColor: brand.primary.blue,
			textLabelColor: brand.primary.white,
			textColor: brand.primary.blue,
		},

		[TypesOfPlansEnum.ADVANCE]: {
			backgroundImage: `url(${walletImageAdvance})`,
			invertColor: true,
			invertPlanTypeColor: brand.primary.white,
			textLabelColor: brand.primary.white,
			textColor: brand.primary.blue,
		},

		[TypesOfPlansEnum.INFINITY]: {
			backgroundImage: `url(${walletImageInfinity})`,
			invertColor: true,
			invertPlanTypeColor: brand.primary.white,
			textLabelColor: '#88B9D7',
			textColor: brand.primary.white,
			border: '1px solid #4D587A',
		},

		[TypesOfPlansEnum.OURO]: {
			backgroundImage: `url(${walletImageGold})`,
			headerBackgroundImage: `url(${walletHeaderGold})`,
			invertColor: true,
			invertPlanTypeColor: brand.primary.white,
			textLabelColor: brand.primary.white,
			textColor: brand.primary.blue,
			plainHeader: true,
			textSize: '1rem',
		},
		[TypesOfPlansEnum.PRATA]: {
			backgroundImage: `url(${walletImageSilver})`,
			headerBackgroundImage: `url(${walletHeaderSilver})`,
			invertColor: true,
			invertPlanTypeColor: brand.primary.white,
			textLabelColor: brand.primary.white,
			textColor: brand.primary.white,
			plainHeader: true,
			textSize: '1rem',
		},
		[TypesOfPlansEnum.DIAMANTE]: {
			backgroundImage: `url(${walletImageDiamond})`,
			headerBackgroundImage: `url(${walletHeaderDiamond})`,
			invertColor: true,
			invertPlanTypeColor: brand.primary.white,
			textLabelColor: '#88B9D7',
			textColor: brand.primary.white,
			plainHeader: true,
			textSize: '1rem',
		},
	};

	const getPlanConfig = (cdModeloCarteira: string | null) => {
		const planType = cdModeloCarteira as SupportedPlanType;
		const isNewWallet = NEW_WALLET_PLAN_TYPES.includes(planType);

		if (isNewWallet && !isNewWalletsEnabled) {
			return planTypeMap[TypesOfPlansEnum.NOTREDAME_SAUDE];
		}

		return planTypeMap[planType] ?? planTypeMap[TypesOfPlansEnum.NOTREDAME_SAUDE];
	};

	const planConfig = getPlanConfig(cdModeloCarteira);

	return (
		<Fade in timeout={1500}>
			<StyledCard
				elevation={0}
				sx={{
					border: planConfig.border || 'none',
				}}>
				<StyledBox
					sx={{
						backgroundImage: planConfig.backgroundImage,
					}}>
					{!isValid || isContractSuspended ? (
						<AlertInfo.Root
							severity="warning"
							sx={{
								padding: '0 8px 0 8px',
								borderRadius: '6px 6px 0 0',
								m: '-16px -16px 0px -16px',
							}}>
							<AlertInfo.Description>
								<Typography fontSize={'0.875rem'} component={'span'}>
									{alertLabel}
									<Typography
										component={'span'}
										fontSize={'inherit'}
										fontWeight={700}>
										{alertDate
											? stringfyDate(alertDate, DateFormatEnum.DATE_FORMAT_8)
											: ''}
									</Typography>
								</Typography>
							</AlertInfo.Description>
						</AlertInfo.Root>
					) : null}

					<StyledCardHeader
						$plainHeader={planConfig.plainHeader}
						sx={{
							backgroundImage: planConfig.headerBackgroundImage || 'none',
						}}>
						<ProductLogo
							customHeight={40}
							invertColor={planConfig.invertColor}
							invertPlanTypeColor={planConfig.invertPlanTypeColor}
						/>

						{planConfig.plainHeader ? (
							<Stack alignItems="flex-end" width="100%">
								<Stack alignItems="flex-end">
									<Typography variant="h5" color={brand.primary.white}>
										{planTypeOptions[tipoPlanoC].label}
									</Typography>
									<Divider
										sx={{
											backgroundColor: brand.primary.white,
											width: '100%',
										}}
									/>
									<Typography variant="caption" color={brand.primary.white}>
										{formattedChipLabel}
									</Typography>
								</Stack>
							</Stack>
						) : (
							<Stack gap="10px">
								<StyledChip label={formattedChipLabel} />
								<TagChip
									label={planTypeOptions[tipoPlanoC].label}
									color={planTypeOptions[tipoPlanoC].color}
								/>
							</Stack>
						)}
					</StyledCardHeader>

					<Stack gap="12px">
						<Stack
							direction="row"
							alignItems="baseline"
							justifyContent="space-between"
							gap={isMobile ? 1 : 2}>
							<ListItemText
								slotProps={{
									primary: {variant: 'caption', color: planConfig.textLabelColor},
									secondary: {
										variant: 'body2',
										color: planConfig.textColor,
										fontSize: planConfig.textSize || '0.875rem',
										fontWeight: 500,
									},
								}}
								primary={name}
								secondary={formattedUserName}
								sx={{width: '50%', m: 0}}
							/>

							<ListItemText
								slotProps={{
									primary: {variant: 'caption', color: planConfig.textLabelColor},
									secondary: {
										variant: 'body2',
										color: planConfig.textColor,
										fontSize: planConfig.textSize || '0.875rem',
										fontWeight: 500,
									},
								}}
								primary={plan}
								secondary={nmPlano}
								sx={{width: '50%', m: 0}}
							/>
						</Stack>

						<Stack
							direction="row"
							alignItems="baseline"
							justifyContent="space-between"
							gap={isMobile ? 1 : 0}>
							<ListItemText
								slotProps={{
									primary: {variant: 'caption', color: planConfig.textLabelColor},
									secondary: {
										variant: 'body2',
										color: planConfig.textColor,
										fontSize: planConfig.textSize || '0.875rem',
										fontWeight: 500,
									},
								}}
								primary={cardCode}
								secondary={cdUsuarioC}
								sx={{width: '50%', m: 0}}
							/>
							{!hiddenCopyIcon && <CopyButton value={cdUsuarioC} />}
							<ListItemText
								slotProps={{
									primary: {variant: 'caption', color: planConfig.textLabelColor},
									secondary: {
										variant: 'body2',
										color: planConfig.textColor,
										fontSize: planConfig.textSize || '0.875rem',
										fontWeight: 500,
									},
								}}
								primary={network}
								sx={{width: '50%', m: 0}}
								secondary={redeCarteirinha}
							/>
						</Stack>
					</Stack>
				</StyledBox>
			</StyledCard>
		</Fade>
	);
}
